import axios from "axios";
import { get_headers, CREDIT_REPORT_VERIFICATION_URL, CREDIT_REPORT_VERIFIED_DATA } from "@/utils/constants/constants.js";

/**
 * Detects if current context is lead or application
 * @returns {Object} Context type and ID
 */
function getVerificationContext() {
  const application_form_id = sessionStorage.getItem("application_form_id");
  const lead_form_id = sessionStorage.getItem("lead_form_id");
  const lead_id = sessionStorage.getItem("lead_id");

  if (lead_form_id || lead_id) {
    return { 
      type: 'lead', 
      lead_form_id: lead_form_id || null,
      lead_id: lead_id || null 
    };
  }
  
  return { 
    type: 'application', 
    application_form_id: application_form_id || null 
  };
}

/**
 * Parses error responses from Axios and returns a user-friendly message.
 * @param {Object} error - Axios error object
 * @param {string} fallbackMessage - Default message if no specific error is found
 * @returns {string} - Parsed error message
 */


/**
 * Build query parameters for Credit Report verification
 * @param {Object} params - Input parameters
 * @returns {Object} Query parameters or error object
 */
function buildQueryParams(params) {
  if (!params.service_id) {
    return {
      error: true,
      response: {
        status: "error",
        message: "Service ID is required",
        results: [],
        service_name: "Credit Report Verification",
      }
    };
  }

  const query_params = { service_id: params.service_id };

  if (params.entity_profile_id) {
    query_params.entity_profile_id = params.entity_profile_id;
  } else if (params.lead_id) {
    query_params.lead_id = params.lead_id;
  } else {
    const entity_id = sessionStorage.getItem("entity_profile_id");
    const lead_id = sessionStorage.getItem("lead_id");
    
    if (entity_id) {
      query_params.entity_profile_id = entity_id;
    } else if (lead_id) {
      query_params.lead_id = lead_id;
    } else {
      return {
        error: true,
        response: {
          status: "error",
          message: "Either entity_profile_id or lead_id is required",
          results: [],
          service_name: "Credit Report Verification",
        }
      };
    }
  }

  return { error: false, query_params };
}

/**
 * Normalize verification status
 * @param {string} status - Raw status from API
 * @returns {string} Normalized status ('success' or 'fail')
 */
function normalizeStatus(status) {
  const status_text = (status || "").toLowerCase();
  return status_text.includes("success") ||
         status_text.includes("pass") ||
         status_text.includes("verified") ||
         status_text.includes("completed")
    ? "success"
    : "fail";
}

/**
 * Parse and format credit report results data
 * @param {Object} data - Raw API response data
 * @returns {Object} Formatted results data
 */
function parseResultsData(data) {
  const raw_results_data = data.results_data || data.data || data.details || {};
  const consumer = raw_results_data.Consumer || {};

  // Consumer Detail (flat)
  const consumer_detail = consumer.ConsumerDetail || {};

  // Other Sections (arrays / objects)
  const additional_sections = {};
  Object.entries(consumer).forEach(([key, value]) => {
    if (key !== "ConsumerDetail") {
      Reflect.set(additional_sections, key, value);
    }
  });

  // Flatten into a structure your template already supports
  return {
    "Consumer Detail": consumer_detail,
    ...additional_sections,
  };
}

/**
 * Format successful verification response
 * @param {Object} data - API response data
 * @returns {Object} Formatted response
 */
function formatSuccessResponse(data) {
  const normalized_status = normalizeStatus(data.status);
  const default_message = normalized_status === "fail" ? "System verification failed." : "";
  const formatted_results_data = parseResultsData(data);

  const result_object = {
    status: normalized_status,
    message: data.message || default_message,
    field_results: data.field_results || {},
    results_data: formatted_results_data,
    service_name: "Credit Report Verification",
  };

  return {
    status: normalized_status,
    results: [result_object],
    raw_data: data,
    service_name: "Credit Report Verification",
  };
}

/**
 * Format error response
 * @param {Object} data - Error data from API
 * @returns {Object} Formatted error response
 */
function formatErrorResponse(data) {
  return {
    status: "error",
    message: data.message,
    results: [{
      status: "error",
      message: data.message,
      field_results: {},
      results_data: {},
      service_name: "Credit Report Verification",
    }],
    raw_data: data,
    service_name: "Credit Report Verification",
  };
}

/**
 * Credit Report Verification API function
 * @param {Object} payload - Contains service_id, form_id, context
 * @returns {Promise} - API response
 */
export const creditreportVerify = async (payload) => {
  const headers = get_headers();
  
  const context = getVerificationContext();
  const enrichedPayload = {
    ...payload,
    ...context
  };

  const response = await axios.post(CREDIT_REPORT_VERIFICATION_URL, enrichedPayload, { headers });
  return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Get Credit Report Verification Results
 * @param {Object} params - Contains service_id AND either entity_profile_id OR lead_id
 * @returns {Promise} - API response with verification results
 */
export const getCreditReportVerificationResults = async (params = {}) => {
  const headers = get_headers();

  try {
    const queryResult = buildQueryParams(params);
    if (queryResult.error) {
      return queryResult.response;
    }

    const response = await axios.get(CREDIT_REPORT_VERIFIED_DATA, {
      headers,
      params: queryResult.query_params,
    });

    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

    if (data.status === "error") {
      return formatErrorResponse(data);
    }

    return formatSuccessResponse(data);
  } catch (error) {
    return {
      status: "error",
      message:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch verification results",
      results: [],
      raw_data: null,
      service_name: "Credit Report Verification",
    };
  }
};